import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Globals } from 'src/app/globals';
import { showFeedbackError, showFeedbackSaved } from 'src/app/core/util/notification';
import { finalize } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';
import { SelectGeoJsonFileModalComponent } from '../select-geo-json-file-modal/select-geo-json-file-modal.component';
import { modalConfig, ModalEnum } from 'src/app/core/util/modalConfig';
import { applyRequiredColumnMarking } from 'src/app/core/util/excel.utility';
import { getDropdownValuesForColumn, isDropdownColumn as checkIsDropdownColumn, isDateColumn, mapDropdownValueForBackend, normalize, isRequiredHeader, isRowCompletelyEmpty } from 'src/app/core/util/dropdown.utility';
// import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import Handsontable from 'handsontable';
import { DocumentsService } from 'src/app/core/service/api/documents.service';
import { GenericInfoModalComponent } from '../generic-info-modal/generic-info-modal.component';
import { getSheetTranslationKey, translateHeader } from 'src/app/core/util/translateExcelHeader.utility';
import { translate, TranslocoService } from '@jsverse/transloco';
import circle from '@turf/circle';
import { calculatePolygonCentroid } from 'src/app/core/util/calculatePolygonCentroid';
import {
  buildGeoJsonPreviewPayloads,
  GeoJsonStreamError,
  streamGeoJsonFeatures,
} from 'src/app/core/util/stream-geojson-features';

@Component({
  selector: 'app-preview-geo-json-modal',
  templateUrl: './preview-geo-json-modal.component.html',
  styleUrl: './preview-geo-json-modal.component.sass',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PreviewGeoJsonModalComponent {
  displayedColumns: string[] = ['rijNummer', 'sheet', 'heading', 'waarde', 'error'];
  displayedErrorsColumns: string[] = ['item_layer', 'item_name', 'errors'];
  dataSource = [
    { rijNummer: 3, heading: 'Telefoonnummer', waarde: '61237', error: 'Incorrecte waarde' },
    { rijNummer: 12, heading: 'Stad', waarde: 'Geen data', error: 'Geen data' },
    { rijNummer: 51, heading: 'Huisnummer', waarde: 'Elf', error: 'Incorrecte data type' },
    { rijNummer: 3, heading: 'Telefoonnummer', waarde: 'Geen data', error: 'Incorrecte waarde' },
    { rijNummer: 3, heading: 'Telefoonnummer', waarde: 'Geen data', error: 'Incorrecte waarde' },
    { rijNummer: 3, heading: 'Telefoonnummer', waarde: 'Geen data', error: 'Incorrecte waarde' }
  ];
  public getSheetTranslationKey: (sheetName: string) => string = getSheetTranslationKey;
  items = [
    { name: 'Stakeholders', color: '#3f51b5', enabled: true, type: 'stakeholders' },
  ];
  public isExcelContentValid = true;
  requirementsLocations: any = [];
  calendarsLocations: any = [];
  showMap: boolean = false;
  isImporting = false;
  isFileValid: boolean | null = null;
  isValidating = false;
  /** True after GeoJSON client stream finished successfully (confirm gate). */
  isGeoJsonStreamComplete = false;
  previewCapped = false;
  previewTotalFeatures: number | null = null;
  previewShownFeatures = 0;
  previewStakeholderTotal = 0;
  previewSkippedUnsupported = 0;
  isExcelPreviewLoading = signal(false);
  isInfoModalOpened = false;
  selectedTabIndex = 0;
  errorsData = [];
  allErrors: any = {};
  geojsonData: any[] = [];
  customData: any[] = [];
  spreadsheet!: any;
  // workbook: XLSX.WorkBook | null = null;
  workbook: ExcelJS.Workbook | null = null;
  updatedExcelFile: File | null = null;
  sheetNames: string[] = [];
  currentSheetData: any[][] = [];
  selectedSheet: string = '';
  private sheetDataMap = new Map<string, any[][]>();
  private sheetValidationMap = new Map<string, boolean>();
  private clientErrorsMap = new Map<string, any[]>();
  public dropdownColumnIndices: Set<number> = new Set(); // Cache dropdown column indices

  private cacheDropdownColumns(): void {
    this.dropdownColumnIndices.clear();
    if (!this.currentSheetData[0]) {
      return;
    }
    
    // Check if this sheet has dropdown configurations
    this.currentSheetData[0].forEach((header, index) => {
      if (checkIsDropdownColumn(this.selectedSheet, header)) {
        this.dropdownColumnIndices.add(index);
      }
    });
  }

  public buildColumnsConfig(): any[] {
    if (!this.currentSheetData[0]) return [];

    return this.currentSheetData[0].map((header, index) => {
      const config: any = {};
      // Check if this column should have a dropdown (values translated by language)
      const dropdownValues = getDropdownValuesForColumn(this.selectedSheet, header, this.translocoService.getActiveLang());
      if (dropdownValues) {
        const isRequired = isRequiredHeader(header);
        config.type = 'dropdown';
        config.source = isRequired ? dropdownValues: ['', ...dropdownValues];
        config.strict = true;
        config.allowEmpty = true;
        config.allowInvalid = false; // Reject any value not in the dropdown list
        config.trimDropdown = false;
      }
      
      return config;
    });
  }

  private calculateColumnWidth(headerText: string): number {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return 150;
    
    // Match the font used in your headers
    context.font = '14px Arial'; // Adjust to match your actual font
    const metrics = context.measureText(headerText);
    const padding = 60; // for padding, icons, and filter buttons
    const minWidth = 150;
    const maxWidth = 400;
    
    return Math.min(Math.max(metrics.width + padding, minWidth), maxWidth);
  }

  settings: Handsontable.GridSettings = {
    rowHeaders: true,
    contextMenu: true,
    filters: true,
    dropdownMenu: true,
    stretchH: 'none',
    colWidths: (index: number) => {
      const header = this.currentSheetData?.[0]?.[index];
      const translatedHeader = header ? translateHeader(header) : '';
      return this.calculateColumnWidth(translatedHeader);
    },
    width: '100%',
    autoColumnSize: {
      samplingRatio: 1,   // measure real content
      useHeaders: true    // measure header text (row 0)
    },
    licenseKey: 'non-commercial-and-evaluation',
    invalidCellClassName: 'htInvalid',
    colHeaders: (index: number) => {
      const header = this.currentSheetData?.[0]?.[index];
      const name = translateHeader(header);
      return name;
    },
    afterChange: (changes, source) => {
      if (source === 'loadData' || !changes) return;

      // changes is an array of [row, prop, oldValue, newValue]
      changes.forEach(([row, prop, oldValue, newValue]) => {
        this.onCellEdit(row, prop, newValue, oldValue);
      });
    },
    beforeChange: (changes) => {
      if (!changes) return;
      
      // Filter out header row changes
      const validChanges = changes.filter((change) => {
        if (!change) return false;
        const [row] = change;
        return row !== 0; // Skip header row
      });

      // Replace the changes array content
      changes.length = 0;
      changes.push(...validChanges);

      if (!changes.length) return false; // Return false to cancel if no valid changes

      // Validate each change
      changes.forEach(([row, col, oldValue, newValue]) => {
        // Skip header row (row 0)
        if (row === 0) return;
        
        const header = this.currentSheetData[0]?.[col as number];
        if (!header) return;
        
        // Date columns: only allow empty or valid date (reject free text)
        if (isDateColumn(header)) {
          const isEmpty = newValue === null || newValue === undefined || newValue === '';
          const isDateObj = newValue instanceof Date;
          const isParseableDate = typeof newValue === 'string' && newValue.trim() !== '' && this.parseDateToDDMMYYYY(newValue) != null;
          if (!isEmpty && !isDateObj && !isParseableDate && !this.isInfoModalOpened) {
            changes[changes.indexOf([row, col, oldValue, newValue])] = [row, col, oldValue, oldValue];
            this.openStatusInfoModal(
              translate('imports.invalid_value'),
              'Please enter a valid date (DD-MM-YYYY) or use the calendar.'
            );
            return;
          }
        }
        
        const dropdownValues = getDropdownValuesForColumn(this.selectedSheet, header, this.translocoService.getActiveLang());
        
        // If this is a dropdown column and value is not in the list, reject it
        if (dropdownValues && newValue !== null && newValue !== '' && newValue !== undefined) {
          const normalizedNewValue = newValue.toString().trim();
          const isValidValue = dropdownValues.some(
            val => val.toString().trim().toLowerCase() === normalizedNewValue.toLowerCase()
          );
          
          if (!isValidValue && !this.isInfoModalOpened) {
            changes[changes.indexOf([row, col, oldValue, newValue])] = [row, col, oldValue, oldValue];
            const data = {
              title: translate('imports.invalid_value'),
              body: `Invalid value "${newValue}" for column "${header}". Please select from: ${dropdownValues.join(', ')}`
            };

            this.openStatusInfoModal(data.title, data.body);
          }
        }
      });
    },
    cells: (row, col) => {
      const cellProperties: Partial<Handsontable.CellProperties> = {};
      cellProperties.renderer = this.errorCellRenderer;
      
      if (row === 0) {
        cellProperties.readOnly = true;
        const header = this.currentSheetData?.[0]?.[col];
        cellProperties.className = (header && isRequiredHeader(header)) ? 'required-excel-header' : 'htBlackColor';
        return cellProperties;
      }

      // Date columns (date, datum, deadline, start_date, end_date) → calendar dropdown, any sheet; reject non-date input
      const headerForDate = this.currentSheetData?.[0]?.[col];
      if (headerForDate && row > 0 && isDateColumn(headerForDate)) {
        cellProperties.type = 'date';
        cellProperties.dateFormat = 'DD-MM-YYYY';
        cellProperties.correctFormat = true;
        cellProperties.allowInvalid = false;

        return cellProperties;
      }

      // Add dropdown validation to cell properties (values translated by language)
      const header = this.currentSheetData[0]?.[col];
      if (header && row > 0) {
        const dropdownValues: any = getDropdownValuesForColumn(this.selectedSheet, header, this.translocoService.getActiveLang());
        if (dropdownValues) {
          const isRequired = isRequiredHeader(header);
          cellProperties.type = 'dropdown';
          cellProperties.source = isRequired ? dropdownValues: ['', ...dropdownValues];
          // cellProperties.strict = true;
          cellProperties.allowInvalid = true;
          cellProperties.allowEmpty = true;
        }
      }
      
      return cellProperties;
    },
  };

  private openStatusInfoModal(title: string | null, body: string | null): void {
    if (this.isInfoModalOpened) return;

    const modal = this.dialog.open(
        GenericInfoModalComponent,
        modalConfig(
          {
            data: { title, body },
            disableClose: false,
            panelClass: 'customized-dialog-container'
          },
          ModalEnum.ModalSmall
        )
      );

    this.isInfoModalOpened = true;

    modal.afterClosed().subscribe(() => {
      this.isInfoModalOpened = false;
    });
  }
    

  // eslint-disable-next-line
  constructor(private cdRef: ChangeDetectorRef, private translocoService: TranslocoService, private dialog: MatDialog, private dialogRef: MatDialogRef<PreviewGeoJsonModalComponent>, @Inject(MAT_DIALOG_DATA) public data:any, public globals: Globals, private _documentsService: DocumentsService){}

  ngOnInit(): void {
    const name = this.data?.file?.name?.toLowerCase() || '';
    this.data.fileType = name.endsWith('.geojson')
    ? 'geojson'
      : name.endsWith('.json')
      ? 'json'
      : name.endsWith('.xlsx')
      ? 'xlsx'
      : name.endsWith('.xls')
      ? 'xls'
      : 'unknown'; 
    
    if (this.isExcelFile()) {
      this.validateExcelFile();
    } else if (this.data.fileType === 'geojson' || this.data.fileType === 'json') {
      this.streamGeoJsonPreview();
    } else {
      this.isFileValid = false;
      this.isValidating = false;
    }
  }

  /** Excel/sheet: keep server validate=1 flow. */
  validateExcelFile(){
    this.isValidating = true;
    const formData = new FormData();
    formData.append('file', this.data.file);
    formData.append('type', 'sheet');
    formData.append('validate', '1');

    const sessionToken = localStorage.getItem('session_token');
    const headersObj: any = {
      'X-Project': this.globals.projectCode,
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    };
    if (sessionToken) {
      headersObj['X-Session-Token'] = sessionToken;
    }
    const setDefaultHeader = new HttpHeaders(headersObj);

    this._documentsService.saveGeoJsonFiles(formData, setDefaultHeader).pipe(
    finalize(() => {
      this.isValidating = false;
      this.cdRef.detectChanges();
    }))
    .subscribe({
      next: (res: any) => {
        this.isFileValid = res.valid;
        this.selectedTabIndex = res.valid ? 1:0;
        if (res.errors && typeof res.errors === 'object') {
          this.allErrors = res.errors;
          let allErrors: any = res.errors;
          allErrors = Object.entries(res.errors)
            .filter(([_, v]) => Array.isArray(v))
            .flatMap(([key, arr]: [string, any[]]) =>
              arr.map(item => ({ ...item, sheet: key }))
          );

          this.errorsData = allErrors;
        } else {
          this.errorsData = [];
        }
        this.isExcelPreviewLoading.set(true);
        this.processFileAfterValidation();
      },
      error: (err) => {
        this.isFileValid = false;
        showFeedbackError(err.message);
      }
    });
  }

  /** GeoJSON: client stream only — no server validate=1. */
  async streamGeoJsonPreview(): Promise<void> {
    this.isValidating = true;
    this.isGeoJsonStreamComplete = false;
    this.isFileValid = null;
    this.previewCapped = false;
    this.previewTotalFeatures = null;
    this.previewShownFeatures = 0;
    this.previewStakeholderTotal = 0;
    this.previewSkippedUnsupported = 0;
    this.errorsData = [];
    this.selectedTabIndex = 1;
    this.cdRef.detectChanges();

    try {
      const result = await streamGeoJsonFeatures(this.data.file);
      if (result.wgs84Error) {
        const message = this.translocoService.translate('imports.wgs84_only_error');
        this.isFileValid = false;
        this.isGeoJsonStreamComplete = true;
        this.selectedTabIndex = 0;
        this.errorsData = [
          {
            item_name: this.data.file?.name || 'GeoJSON',
            errors: message,
          },
        ] as any;
        showFeedbackError(message);
        return;
      }
      const payloads = buildGeoJsonPreviewPayloads(
        result.featuresForPreview,
        calculatePolygonCentroid,
        (pointCoords) =>
          circle(pointCoords, 0.001, { steps: 32, units: 'kilometers' }).geometry.coordinates,
        result.stakeholderFeaturesForPreview
      );
      this.data.geojsonPolygons = payloads.geoPolygons;
      this.data.stakeholders = payloads.stakeholders;
      this.previewCapped = result.capped;
      this.previewTotalFeatures = result.totalFeatures;
      this.previewShownFeatures = result.featuresForPreview.length;
      this.previewStakeholderTotal = result.totalStakeholderFeatures;
      this.previewSkippedUnsupported = result.skippedUnsupportedFeatures;
      this.isFileValid = true;
      this.isGeoJsonStreamComplete = true;
      this.processFileAfterValidation();
    } catch (err: any) {
      this.isFileValid = false;
      this.isGeoJsonStreamComplete = false;
      this.selectedTabIndex = 0;
      const message =
        err instanceof GeoJsonStreamError || err?.message
          ? err.message
          : translate('imports.unsupported_geometry');
      this.errorsData = [
        {
          item_name: this.data.file?.name || 'GeoJSON',
          errors: message,
        },
      ] as any;
      showFeedbackError(message);
    } finally {
      this.isValidating = false;
      this.cdRef.detectChanges();
    }
  }

  public isExcelFile(): boolean {
    return ['xls', 'xlsx'].includes(this.data?.fileType);
  }

  private processFileAfterValidation(){
    if (this.data.fileType === 'geojson' || this.data.fileType === 'json') {
      this.updateGeoJsonData();
      this.customData.push({
        name: this.data.file?.name || 'Uploaded File',
        color: '#2196f3',
        enabled: true,
        type: 'custom'
      });
      setTimeout(() => {
        this.showMap = true;
        this.cdRef.detectChanges();
      }, 50);
    } else if (this.data.fileType === 'xlsx' || this.data.fileType === 'xls') {
      this.processExcelFile(this.data.file);
    }
    this.cdRef.detectChanges();
  }

  // private processExcelFile(file: File): void {
  //   if (!file) return;
    
  //   const reader = new FileReader();
  //   reader.onload = (e: any) => {
  //     const data = new Uint8Array(e.target.result);
  //     this.workbook = XLSX.read(data, { type: 'array' });
  //     this.sheetNames = this.workbook.SheetNames;
  //     this.loadSheet(this.sheetNames[0]);
  //   };
  //   reader.readAsArrayBuffer(file);
  // }
  
  private async processExcelFile(file: File): Promise<void> {
    if (!file) return;

    try {
      const buffer = await this.readFileAsArrayBuffer(file);
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(buffer);

      this.workbook = workbook as any; // keep same property name, but now it's ExcelJS.Workbook
      const allNames = workbook.worksheets.map(ws => ws.name);
      // Exclude any sheet named "Instructies" or "instructions", regardless of position
      this.sheetNames = allNames.filter(name => {
        const normalized = name?.trim().toLowerCase();
        return normalized !== 'instructies' && normalized !== 'instructions';
      });

      this.loadAllSheetsData(workbook);

      if (this.sheetNames.length > 0) {
        this.loadSheet(this.sheetNames[0]);
      }
    } catch (err) {
      this.isExcelPreviewLoading.set(false);
      this.cdRef.detectChanges();
      showFeedbackError(err?.message || 'Failed to load Excel preview');
      return;
    } finally {
      this.isExcelPreviewLoading.set(false);
      this.cdRef.detectChanges();
    }
  }

  private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => resolve(e.target.result as ArrayBuffer);
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });
  }

  private loadAllSheetsData(workbook: ExcelJS.Workbook): void {
    const MIN_ROWS = 20;
    const MIN_COLS = 20;

    this.sheetDataMap.clear();

    workbook.worksheets.forEach((ws, index) => {
      // Skip any sheet that is "Instructies" or "instructions"
      const sheetName = ws.name?.trim().toLowerCase();
      if (sheetName === 'instructies' || sheetName === 'instructions') {
        return;
      }
      const jsonData: any[][] = [];

      ws.eachRow({ includeEmpty: true }, (row) => {
        const rowData: any[] = [];

        row.eachCell({ includeEmpty: true }, (cell) => {
          if (cell.value instanceof Date) {
            rowData.push(cell.value);
          } else {
            rowData.push(cell.value ?? '');
          }
        });

        jsonData.push(rowData);
      });

      /** Determine max columns */
      const maxCols = Math.max(
        ...jsonData.map(r => r.length),
        MIN_COLS
      );

      /** Normalize columns */
      jsonData.forEach(row => {
        while (row.length < maxCols) row.push('');
      });

      /** Normalize rows */
      while (jsonData.length < MIN_ROWS) {
        jsonData.push(new Array(maxCols).fill(''));
      }

      /** Apply required column marking */
      applyRequiredColumnMarking(ws.name, jsonData);

      /** Normalize date columns to DD-MM-YYYY */
      this.normalizeDateColumnsInSheetData(jsonData);

      /** Cache sheet data */
      this.sheetDataMap.set(ws.name, jsonData);
    });
  }

  // Load specific sheet data
  // loadSheet(sheetName: string): void {
  //   if (!this.workbook) return;
  //   const ws = this.workbook.Sheets[sheetName];
  //   const jsonData = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false });
  //   const MIN_ROWS = 20;
  //   const MIN_COLS = 20;
  //   const maxCols = Math.max(...jsonData.map((r: any) => r.length), MIN_COLS);
  //   jsonData.forEach((row: any) => {
  //     while (row.length < maxCols) row.push('');
  //   });
  //   while (jsonData.length < MIN_ROWS) {
  //     jsonData.push(new Array(maxCols).fill(''));
  //   }
  //   this.currentSheetData = jsonData as any;
  //   this.selectedSheet = sheetName;
  //   this.cdRef.detectChanges();
  // }

  loadSheet(sheetName: string): void {
    if (this.sheetDataMap.has(sheetName)) {
      this.currentSheetData = this.sheetDataMap.get(sheetName)!;
      this.selectedSheet = sheetName;

      this.cacheDropdownColumns();
      this.validateAllSheets();
      this.cdRef.detectChanges();
      return;
    }

    if (!this.workbook) return;

    const ws = this.workbook.getWorksheet(sheetName);
    if (!ws) return;

    const jsonData: any[][] = [];

    ws.eachRow({ includeEmpty: true }, (row, rowNumber) => {
      const rowData: any[] = [];
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        if (cell.value instanceof Date) {
          rowData.push(cell.value); // keep Date object
        } else {
          rowData.push(cell.value ?? '');
        }
      });
      jsonData.push(rowData);
    });

    const MIN_ROWS = 20;
    const MIN_COLS = 20;
    const maxCols = Math.max(...jsonData.map(r => r.length), MIN_COLS);

    jsonData.forEach((row: any) => {
      while (row.length < maxCols) row.push('');
    });
    while (jsonData.length < MIN_ROWS) {
      jsonData.push(new Array(maxCols).fill(''));
    }

    /** Normalize date columns to DD-MM-YYYY */
    this.normalizeDateColumnsInSheetData(jsonData);

    this.currentSheetData = jsonData;
    this.selectedSheet = sheetName;
    this.sheetDataMap.set(sheetName, jsonData);
    this.cacheDropdownColumns(); // Cache dropdown columns once when sheet is loaded
    this.validateAllSheets();
    this.cdRef.detectChanges();

    setTimeout(() => {
    const hot = this.spreadsheet;
      if (hot) {
        hot.getPlugin('autoColumnSize').recalculateAllColumns();
        hot.render();
      }
    });
  }

  close(){
    this.dialogRef.close();
  }

  async saveGeoJsonFile(){
    if (this.isImporting) return;
    if (!this.isExcelFile() && (!this.isGeoJsonStreamComplete || !this.isFileValid)) {
      return;
    }
    this.isImporting = true;
    if (!this.data || !this.data?.file || !this.data?.fileType) {
      showFeedbackError('No file selected for upload.');
      this.isImporting = false;
      return;
    }

    if (this.isExcelFile() && !this.updatedExcelFile) {
      await this.updateExcelFile();
    }

    const formData = new FormData();
    formData.append('file', this.updatedExcelFile ? this.updatedExcelFile : this.data.file);
    formData.append('type', this.isExcelFile() ? 'sheet' : 'geojson' );
    // GeoJSON: never send validate=1 — accept path stores S3 and queues the job.

    const sessionToken = localStorage.getItem('session_token');
    const headersObj: any = {
      'X-Project': this.globals.projectCode,
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    };
    if (sessionToken) {
      headersObj['X-Session-Token'] = sessionToken;
    }
    const setDefaultHeader = new HttpHeaders(headersObj);

    this._documentsService.saveGeoJsonFiles(formData, setDefaultHeader).pipe(
    finalize(() => {
      this.isImporting = false;
      this.cdRef.detectChanges();
    }))
    .subscribe({
      next: (res: any) => {
        const onImportSuccess = this.data?.onImportSuccess;
        const processId = res.status === 'processing' ? res?.metadata?.process_id : null;
        this._documentsService.triggerRefresh(processId);
        this.close();
        if (typeof onImportSuccess === 'function') {
          setTimeout(() => onImportSuccess(processId), 0);
        }
        showFeedbackSaved();
      },
      error: (err) => {
        showFeedbackError(err.message);
      }
    });
  }

  onToggle(item: any) {
    if (item.type === 'custom') {
      if (item.enabled) {
        this.geojsonData = [...this.geojsonData, ...this.data.geojsonPolygons];
      } else {
        this.geojsonData = this.geojsonData.filter(
          d => !this.data.geojsonPolygons.includes(d)
        );
      }
    } else {
      this.updateGeoJsonData();
    }
    this.cdRef.detectChanges();
  }

  private updateGeoJsonData() {
    const activeTypes = this.items.filter(i => i.enabled).map(i => i.type);
    // const updatedData = [];
    const updatedData = [...this.data.geojsonPolygons];

    // Add data conditionally based on toggle type
    if (activeTypes.includes('stakeholders') && this.data.stakeholders.length) updatedData.push(...this.data.stakeholders);
    // if (activeTypes.includes('issues') && this.issuesLocations.length) updatedData.push(this.issuesLocations);
    // if (activeTypes.includes('complaints') && this.complaintsLocations.length) updatedData.push(...this.complaintsLocations);
    // if (activeTypes.includes('requirements') && this.requirementsLocations.length) updatedData.push(...this.requirementsLocations);
    // if (activeTypes.includes('calendars') && this.calendarsLocations.length) updatedData.push(...this.calendarsLocations);
    this.geojsonData = [...updatedData];
  }

  goFullscreen() {
    const buttons = document.querySelectorAll<HTMLElement>('.ol-full-screen button');
    if (buttons && buttons.length > 1) {
      buttons[1].click();
    }
  }

  openGeoJsonSelectModal(){
    this.dialogRef.close();
    const dialog = this.dialog.open(SelectGeoJsonFileModalComponent, modalConfig({
      data: {type: 'excel'},
      panelClass: 'customized-dialog-container',
    }, ModalEnum.ModalMedium));
  }

  private errorCellRenderer = (
    instance: any,
    td: HTMLTableCellElement,
    row: number,
    col: number,
    prop: any,
    value: any,
    cellProperties: any
  ) => {
    Handsontable.renderers.TextRenderer.call(this, instance, td, row, col, prop, value, cellProperties);
    if (value instanceof Date) {
      td.innerText = this.formatDate(value);
      return;
    }

    // Handle header row (row 0) - style asterisks in red
    if (row === 0 && value && typeof value === 'string' && value.includes('*')) {
      const headerText = value.replace('*', '');
      td.innerHTML = `${headerText}<span style="color: red; font-weight: bold;">*</span>`;
      return;
    }

    const sheet = this.normalizeSheetName(this.selectedSheet);

    const backendErrors = this.allErrors?.[sheet] || [];
    const clientErrors = this.clientErrorsMap.get(sheet) || [];

    const errors = [...backendErrors, ...clientErrors];
    if (!errors.length) return;

    const headerName = (this.currentSheetData[0]?.[col] || '').toString().trim().toLowerCase();
    const excelRow = row + 1;
    const excelCol = col + 1;

    // Check if this column has a missing_header error anywhere
    const columnHasMissingHeader = errors.some((err: any) =>
      err.type === 'missing_header' && this.isColumnMatch(err, excelCol, headerName)
    );

    // If missing_header is present for this column — ignore ALL error types
    if (columnHasMissingHeader) {
      this.resetCellStyling(td);
      return;
    }

    // Otherwise, find relevant error for this cell
    const error = errors.find((err: any) =>
      err.row === excelRow && this.isColumnMatch(err, excelCol, headerName)
    );

    if (error) {
      this.applyCellErrorStyling(td, error);
    } else {
      this.resetCellStyling(td);
    }
  };


  private isColumnMatch(error: any, excelCol: number, headerName: string): boolean {
    // Priority 1: Match by column_index (most reliable)
    if (error.column_index !== undefined && error.column_index !== null) {
      return error.column_index === excelCol;
    }
    
    // Priority 2: Match by column name
    if (error.column) {
      return error.column.toLowerCase() === headerName;
    }
    
    return false;
  }

  private applyCellErrorStyling(td: HTMLTableCellElement, error: any): void {
    td.style.backgroundColor = '#ffcccc';
    td.style.color = '#000';
    td.style.border = '2px solid red';
    td.style.fontWeight = 'bold';
    td.title = error.message || 'Error';
  }

  private resetCellStyling(td: HTMLTableCellElement): void {
    td.style.backgroundColor = '';
    td.style.color = '';
    td.style.border = '';
    td.style.fontWeight = '';
    td.title = '';
  }

  private formatDate(value: Date): string {
    return this.formatDateToDDMMYYYY(value);
  }

  /** Format Date to DD-MM-YYYY string (used for display and storage). */
  private formatDateToDDMMYYYY(value: Date): string {
    const d = value.getDate().toString().padStart(2, '0');
    const m = (value.getMonth() + 1).toString().padStart(2, '0');
    const y = value.getFullYear().toString();
    return `${d}-${m}-${y}`;
  }

  /** Normalize date columns in sheet data: convert Date or date-like strings to DD-MM-YYYY. */
  private normalizeDateColumnsInSheetData(jsonData: any[][]): void {
    if (!jsonData?.length || !jsonData[0]) return;

    const headers = jsonData[0];
    for (let col = 0; col < headers.length; col++) {
      if (!isDateColumn(headers[col])) continue;

      for (let row = 1; row < jsonData.length; row++) {
        const val = jsonData[row][col];
        if (val == null || val === '') continue;

        if (val instanceof Date) {
          jsonData[row][col] = this.formatDateToDDMMYYYY(val);
        } else if (typeof val === 'string' && val.trim()) {
          const parsed = this.parseDateToDDMMYYYY(val);
          if (parsed != null) jsonData[row][col] = parsed;
        }
      }
    }
  }

  /** Try to parse a value (string or number) to DD-MM-YYYY; return null if not parseable. Accepts DD-MM-YYYY and DD-MM-YY. */
  private parseDateToDDMMYYYY(value: any): string | null {
    if (value instanceof Date) return this.formatDateToDDMMYYYY(value);
    const str = value?.toString?.()?.trim?.();
    if (!str) return null;

    // Already DD-MM-YYYY (e.g. 31-12-2024)
    const ddmmyyyy = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(str);
    if (ddmmyyyy) {
      const day = ddmmyyyy[1].padStart(2, '0');
      const month = ddmmyyyy[2].padStart(2, '0');
      const year = ddmmyyyy[3];
      return `${day}-${month}-${year}`;
    }

    // DD-MM-YY (e.g. 31-12-24) → convert to DD-MM-YYYY (00-29 → 2000-2029, 30-99 → 1930-1999)
    const ddmmyy = /^(\d{1,2})-(\d{1,2})-(\d{2})$/.exec(str);
    if (ddmmyy) {
      const day = ddmmyy[1].padStart(2, '0');
      const month = ddmmyy[2].padStart(2, '0');
      const yy = parseInt(ddmmyy[3], 10);
      const fullYear = yy <= 29 ? 2000 + yy : 1900 + yy;
      return `${day}-${month}-${fullYear}`;
    }

    // ISO or YYYY-MM-DD
    const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(str);
    if (iso) {
      const [, y, m, d] = iso;
      return `${d.padStart(2, '0')}-${m.padStart(2, '0')}-${y}`;
    }

    const date = new Date(str);
    if (!isNaN(date.getTime())) return this.formatDateToDDMMYYYY(date);
    return null;
  }

  onCellEdit(row: number, col: any, newValue: any, oldValue: any) {
    const header = this.currentSheetData[0]?.[col as number];
    if (header && isDateColumn(header)) {
      if (newValue instanceof Date) {
        newValue = this.formatDateToDDMMYYYY(newValue);
      } else if (typeof newValue === 'string' && newValue.trim()) {
        const parsed = this.parseDateToDDMMYYYY(newValue);
        if (parsed != null) newValue = parsed; // normalize DD-MM-YY → DD-MM-YYYY
      }
    }

    // Update your in-memory data
    this.currentSheetData[row][col] = newValue;
    this.sheetDataMap.set(
      this.selectedSheet,
      [...this.currentSheetData]
    );

    this.removeBackendErrorForCell(row, col, header);
    this.validateAllSheets();
    this.isExcelContentValid = Array.from(this.sheetValidationMap.values()).every(v => v);
    this.cdRef.detectChanges();
    // update Excel in memory (not on disk yet)
    this.updateExcelFile();
    this.updateFileValidityStatus();
  }

  private updateFileValidityStatus(): void {
    const hasAnyBackendErrors = Object.values(this.allErrors).some(
      (errors: any) => Array.isArray(errors) && errors.length > 0
    );
    
    if (this.isExcelFile()) {
      this.isFileValid = !hasAnyBackendErrors;
    }
  }

  private removeBackendErrorForCell(row: number, col: number, header: any): void {
    const normalizedSheet = this.normalizeSheetName(this.selectedSheet);
    const backendErrors = this.allErrors?.[normalizedSheet];
    
    if (!Array.isArray(backendErrors) || backendErrors.length === 0) {
      return;
    }

    const excelRow = row + 1;
    const excelCol = col + 1;
    const headerName = header?.toString().trim().toLowerCase();

    // Filter out errors that match this cell
    this.allErrors[normalizedSheet] = backendErrors.filter((err: any) => {
      const rowMatches = err.row === excelRow;
      const colMatches = this.isColumnMatch(err, excelCol, headerName);
      return !(rowMatches && colMatches);
    });

    // Clean up if no errors remain
    if (this.allErrors[normalizedSheet].length === 0) {
      delete this.allErrors[normalizedSheet];
    }

    // **Update errorsData array for the error table**
    this.errorsData = Object.entries(this.allErrors)
      .filter(([_, v]) => Array.isArray(v))
      .flatMap(([key, arr]: [string, any[]]) => 
        arr.map(item => ({ ...item, sheet: key }))
      );
  }

  private validateSheet(sheetName: string, sheetData: any[][]): boolean {
    if (!sheetData?.length) return false;

    const normalizedSheet = this.normalizeSheetName(sheetName);
    const clientErrors: any[] = [];

    const headers = sheetData[0];

    for (let row = 1; row < sheetData.length; row++) {
      const rowData = sheetData[row];

      if (isRowCompletelyEmpty(rowData)) continue;

      for (let col = 0; col < headers.length; col++) {
        const header = headers[col];
        const value = normalize(rowData[col]);

        /** Required field validation */
        if (isRequiredHeader(header) && !value) {
          clientErrors.push({
            row: row + 1,
            column_index: col + 1,
            column: header?.toString().trim().toLowerCase(),
            message: 'Required field is empty'
          });
          continue;
        }

        /** Date column validation: only allow empty or valid date */
        if (isDateColumn(header) && value) {
          const rawVal = rowData[col];
          const isDateObj = rawVal instanceof Date;
          const isParseableDate = this.parseDateToDDMMYYYY(rawVal) != null;
          if (!isDateObj && !isParseableDate) {
            clientErrors.push({
              row: row + 1,
              column_index: col + 1,
              column: header?.toString().trim().toLowerCase(),
              message: 'Invalid date; use DD-MM-YYYY or select from calendar'
            });
          }
          continue;
        }

        /** Dropdown validation (values translated by language) */
        const dropdownValues = getDropdownValuesForColumn(sheetName, header, this.translocoService.getActiveLang());
        if (dropdownValues) {
          const isRequired = isRequiredHeader(header);

          /** Empty value handling */
          if (!value) {
            if (isRequired) {
              clientErrors.push({
                row: row + 1,
                column_index: col + 1,
                column: header?.toString().trim().toLowerCase(),
                message: 'Required field is empty'
              });
            }
            continue; // optional dropdown can be empty
          }

          /** If value exists, it MUST be from dropdown */
          const isValid = dropdownValues.some(
            v => v.toString().trim().toLowerCase() === value.toLowerCase()
          );

          if (!isValid) {
            clientErrors.push({
              row: row + 1,
              column_index: col + 1,
              column: header?.toString().trim().toLowerCase(),
              message: `Invalid value "${value}"`
            });
          };
        }
      }
    }

    /** Backend validation errors */
    const backendErrors = this.allErrors?.[this.normalizeSheetName(sheetName)];
    const hasBackendErrors = Array.isArray(backendErrors) && backendErrors.length > 0;


    this.clientErrorsMap.set(normalizedSheet, clientErrors);

    return clientErrors.length === 0 && !hasBackendErrors;
  }

  private normalizeSheetName(name: string): string {
    return name.trim().toLowerCase();
  }

  private validateAllSheets(): void {
    this.sheetNames.forEach(sheetName => {
      const data =
        sheetName === this.selectedSheet
          ? this.currentSheetData
          : this.sheetDataMap.get(sheetName);

      if (!data) {
        this.sheetValidationMap.set(sheetName, false);
        return;
      }

      const hasBackendErrors =
      Array.isArray(this.allErrors?.[this.normalizeSheetName(sheetName)]) &&
      this.allErrors[this.normalizeSheetName(sheetName)].length > 0;

      if (hasBackendErrors) {
        this.sheetValidationMap.set(sheetName, false);
        return;
      }

      const isValid = this.validateSheet(sheetName, data);
      this.sheetValidationMap.set(sheetName, isValid);
    });

    // Import button logic
    this.isExcelContentValid = Array.from(this.sheetValidationMap.values()).every(v => v);
  }

  // updateExcelFile() {
  //   if (!this.workbook) return;
  //   // Remove completely empty rows before saving
  //   const cleanedData = this.currentSheetData.filter(
  //     (row: any[]) => row.some((cell) => cell !== null && cell !== undefined && cell !== '')
  //   );

  //   // Create worksheet and workbook
  //   const ws = XLSX.utils.aoa_to_sheet(cleanedData);
  //   const wb = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(wb, ws, this.selectedSheet || 'Sheet1');

  //   // Generate file buffer
  //   const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  //   const fileName = this.data.file.name || 'updated_file.xlsx';

  //   // Create File object
  //   const excelFile = new File([excelBuffer], fileName, {
  //     type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  //   });

  //   // Save in component variable
  //   this.updatedExcelFile = excelFile;
  // }

  async updateExcelFile() {
    if (!this.workbook) return;

    const wb = new ExcelJS.Workbook();

    for (const sheetName of this.sheetNames) {
      const sheetData = this.sheetDataMap.get(sheetName);
      if (!sheetData || !sheetData.length) continue;
      const isStakeholdersSheet = this.normalizeSheetName(sheetName) === 'stakeholders';

      // Remove completely empty rows before saving (same logic per sheet)
      const cleanedData = sheetData.filter(
        (row: any[]) => row.some((cell) => cell !== null && cell !== undefined && cell !== '')
      );

      if (cleanedData.length === 0) continue;

      // Remove asterisks from header row (first row)
      cleanedData[0] = cleanedData[0].map((header: any) => {
        if (header && typeof header === 'string') {
          return header.replace('*', '').trim();
        }
        return header;
      });

      const ws = wb.addWorksheet(sheetName);

      cleanedData.forEach((row) => {
        const rowValues: any[] = [];
        row.forEach((cellValue, colIndex) => {
          if (isStakeholdersSheet) {
            const header = cleanedData[0]?.[colIndex];
            rowValues[colIndex + 1] = mapDropdownValueForBackend(sheetName, header, cellValue); // ExcelJS is 1-based
            return;
          }
          rowValues[colIndex + 1] = cellValue; // ExcelJS is 1-based
        });
        ws.addRow(rowValues);
      });
    }

    const excelBuffer = await wb.xlsx.writeBuffer();
    const fileName = this.data.file.name || 'updated_file.xlsx';

    const excelFile = new File([excelBuffer], fileName, {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    this.updatedExcelFile = excelFile;
  }

  hasSheetErrors(sheetName: string): boolean {
    const sheet = this.normalizeSheetName(sheetName);

    const backend = this.allErrors?.[sheet]?.length > 0;
    const client = this.clientErrorsMap.get(sheet)?.length > 0;

    return backend || client;
  }
}
